import { Fragment, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { dayColor, useSchedule, type Schedule } from "../../api/routing";
import "../../styles/routing.css";
import RoutingTabs from "./RoutingTabs";

type Card = Schedule["columns"][number]["cards"][number];

export interface WeekGrid {
  columns: Schedule["columns"];
  crews: { name: string; cells: Card[][] }[];
}

/** The week as crews down the side and days across. Saturday and Sunday only
 * take a column when someone works them, so an ordinary week reads Monday to
 * Friday. A crew appears once it has a day this week. */
export function weekGrid(schedule: Schedule): WeekGrid {
  const columns = schedule.columns.filter((col) => !col.is_weekend || col.cards.length > 0);
  const names: string[] = [];
  for (const col of columns) {
    for (const card of col.cards) {
      if (!names.includes(card.provider_name)) {
        names.push(card.provider_name);
      }
    }
  }
  return {
    columns,
    crews: names.map((name) => ({ name, cells: columns.map((col) => col.cards.filter((card) => card.provider_name === name)) })),
  };
}

/** What became of a day's stops, as the card says it: "1 done", "1 not done",
 * "1 pending" -- only the ones there are. */
export function cardStatus(card: Card): { tone: "ok" | "warn" | "quiet"; text: string }[] {
  return [
    { tone: "ok" as const, count: card.done, word: "done" },
    { tone: "warn" as const, count: card.not_done, word: "not done" },
    { tone: "quiet" as const, count: card.pending, word: "pending" },
  ]
    .filter((part) => part.count > 0)
    .map((part) => ({ tone: part.tone, text: `${part.count} ${part.word}` }));
}

function weekday(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("en-ZA", { weekday: "short" });
}

/** The weekly schedule: every assigned route day on the week it falls in. A
 * day nobody owns yet has no place here; an archived plan is off it. */
export default function SchedulePage() {
  const [params, setParams] = useSearchParams();
  const week = params.get("week");
  const view = useSchedule(week);
  const data = view.data;
  const grid = data ? weekGrid(data) : null;
  return (
    <div className="page-bleed routing-page">
      <RoutingTabs />
      <div className="routing-content">
        <header className="schedule-head">
          <h1>{data ? `Week of ${formatDate(data.monday)}` : "Weekly schedule"}</h1>
          {data ? (
            <div className="schedule-nav">
              <button type="button" className="btn" onClick={() => setParams({ week: data.prev_week })}>
                ‹ Previous
              </button>
              <button type="button" className="btn" onClick={() => setParams({})}>
                This week
              </button>
              <button type="button" className="btn" onClick={() => setParams({ week: data.next_week })}>
                Next ›
              </button>
            </div>
          ) : null}
        </header>
        {view.isPending ? <p className="app-loading">Loading the week…</p> : null}
        {view.isError ? <p className="notice bad">The schedule could not be loaded: {view.error.message}</p> : null}
        {data && grid && (!data.has_days || grid.crews.length === 0) ? (
          <p className="empty-state card">
            No field operator days assigned from {formatDate(data.monday)} to {formatDate(data.sunday)}. Assign the days of a saved plan on the <Link to="/routing">Planner</Link> or on{" "}
            <Link to="/routing/plans">Plans</Link>.
          </p>
        ) : null}
        {grid && grid.crews.length > 0 ? (
          <div className="week-grid" style={{ gridTemplateColumns: `150px repeat(${grid.columns.length}, minmax(130px, 1fr))` }} aria-label="Crew days this week">
            <div className="week-col-head week-corner">
              Field operators
            </div>
            {grid.columns.map((col) => (
              <div key={col.date} className={["week-col-head", col.is_today ? "today" : "", col.is_weekend ? "weekend" : ""].join(" ").trim()}>
                {weekday(col.date)} {Number(col.date.slice(8, 10))}
              </div>
            ))}
            {grid.crews.map((crew) => (
              <Fragment key={crew.name}>
                <div className="week-crew">
                  {crew.name}
                </div>
                {crew.cells.map((cards, i) => (
                  <div key={grid.columns[i].date} className={grid.columns[i].is_weekend ? "weekend" : undefined}>
                    {cards.map((card) => (
                      <Link
                        key={`${card.plan_id}-${card.day_index}`}
                        to={`/routing/plans/${card.plan_id}`}
                        className={card.completed ? "week-card done" : "week-card"}
                        style={{ "--day": dayColor(card.day_index) } as CSSProperties}
                      >
                        <b>{card.plan_name}</b>
                        <span>
                          Day {card.day_index + 1} · {card.stops} stop{card.stops === 1 ? "" : "s"}
                          {card.completed ? " · day complete" : ""}
                        </span>
                        {card.stops > 0 ? (
                          <span className="week-card-status" aria-label="Stop outcomes">
                            {cardStatus(card).map((part) => (
                              <em key={part.text} className={part.tone}>
                                {part.text}
                              </em>
                            ))}
                          </span>
                        ) : null}
                      </Link>
                    ))}
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
